import React, {useState, useEffect} from "react"
import {useWorkspace} from "@/contexts/WorkspaceContext"
import {useAppSelector, useAppDispatch} from "@/store/hooks"
import {selectWorkspaceInvite} from "@/store/workspace/workspaceSelectors"
import {inviteMemberSaga, setInviteError, setWorkspaceAgentSaga} from "@/store/workspace/workspaceSlice"
import {AGENT_USER_ID, AGENT_NAME, PLATE_BOT_AVATAR} from "@/const/constants"
import FormColumn from "@/components/ui/Form/components/FormColumn/FormColumn"
import FormRow from "@/components/ui/Form/components/FormRow/FormRow"
import Input from "@/components/ui/Input/Input"
import Avatar from "@/components/ui/Avatar/Avatar"
import Toggle from "@/components/ui/Toggle/Toggle"
import WorkspaceSettingsTitle from "@/components/modals/WorkspaceSettingsModal/components/WorkspaceSettingsTitle/WorkspaceSettingsTitle"
import FormTitle from "@/components/ui/Form/components/FormTitle/FormTitle"
import MemberRow from "./components/MemberRow/MemberRow"
import FormDivider from "@/components/ui/Form/components/FormDivider/FormDivider.tsx";
import PlateLoader from "@/components/ui/Loader/PlateLoader"
import css from './WorkspaceSettingsModalMembers.module.css'
import {TextMidd, TextNormBold} from "@/components/ui/Text";

const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

const WorkspaceSettingsModalMembers = () => {
    const dispatch = useAppDispatch()
    const {members, workspace} = useWorkspace()
    const currentUserId = useAppSelector(state => state.auth?.id)
    const invite = useAppSelector(selectWorkspaceInvite)

    const [inviteEmail, setInviteEmail] = useState('')
    const [openRoleDropdownId, setOpenRoleDropdownId] = useState<string | null>(null)
    const [pendingAgent, setPendingAgent] = useState<boolean | null>(null)

    const currentUserMember = members.find(m => m.userId === currentUserId)
    const isCurrentUserOwner = currentUserMember?.role === 'owner'

    const agentMember = members.find(m => m.userId === AGENT_USER_ID)
    const isAgentEnabled = !!agentMember
    // Optimistic: flip the toggle immediately on click, then fall back to the real
    // membership data once the listener catches up (avoids the ~1-2s round-trip lag).
    const agentChecked = pendingAgent ?? isAgentEnabled

    useEffect(() => {
        if (pendingAgent !== null && pendingAgent === isAgentEnabled) setPendingAgent(null)
    }, [isAgentEnabled, pendingAgent])

    const handleAgentToggle = (v: boolean) => {
        setPendingAgent(v)
        // Revert the optimistic toggle if the call fails (otherwise it sticks,
        // since the real membership data never changes on failure).
        dispatch(setWorkspaceAgentSaga({ enabled: v, onError: () => setPendingAgent(null) }))
    }

    // The agent renders in its own section, not the human member list.
    const activeMembers = members.filter(m => m.status === 'active' && m.userId !== AGENT_USER_ID)
    const suspendedMembers = members.filter(m => m.status === 'suspended')
    const pendingMembers = members.filter(m => m.status === 'pending')

    const handleInvite = () => {
        dispatch(setInviteError(null))

        const email = inviteEmail.trim()
        if (!email) {
            dispatch(setInviteError('Please enter an email address'))
            return
        }

        if (!isValidEmail(email)) {
            dispatch(setInviteError('Please enter a valid email address'))
            return
        }

        if (!workspace?.id) {
            dispatch(setInviteError('Workspace not found'))
            return
        }

        dispatch(inviteMemberSaga({ email, addToAllProjects: true }))
        setInviteEmail('')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleInvite()
        }
    }

    return (
        <>
            <WorkspaceSettingsTitle>Members</WorkspaceSettingsTitle>
            <FormDivider />
            <FormColumn gap={16}>
                <FormTitle>Active</FormTitle>
                <FormColumn gap={16}>
                    {activeMembers.map(member => (
                        <MemberRow
                            key={member.userId}
                            member={member}
                            openRoleDropdownId={openRoleDropdownId}
                            isCurrentUserOwner={isCurrentUserOwner}
                            onOpenRoleDropdown={setOpenRoleDropdownId}
                        />
                    ))}
                </FormColumn>
            </FormColumn>
            {isCurrentUserOwner && (
                <div className={css.invite_member}>
                    <Input
                        value={inviteEmail}
                        onChange={(e) => {
                            setInviteEmail(e.target.value)
                            dispatch(setInviteError(null))
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Invite by email"
                        disabled={invite.loading}
                    />
                    {invite.error && (
                        <div style={{color: 'var(--red)', fontSize: '13px', marginTop: '8px'}}>{invite.error}</div>
                    )}
                    <div className={css.invite_note}>
                        <TextMidd>
                            <strong>Note:</strong> Invited members will have access to all projects. You can
                            adjust access later.
                        </TextMidd>
                    </div>
                    {invite.loading && (
                        <div className={css.invite_loader}>
                            <PlateLoader />
                        </div>
                    )}
                </div>
            )}

            {(isCurrentUserOwner || isAgentEnabled) && (
                <>
                    <FormDivider />
                    <FormColumn gap={16}>
                        <FormTitle>AI agent</FormTitle>
                        <FormRow align={'space-between'} gap={10}>
                            <FormRow width={40}>
                                <Avatar
                                    size={'medium2'}
                                    title={AGENT_NAME}
                                    color={'#6366f1'}
                                    avatar={agentMember?.avatarImage || PLATE_BOT_AVATAR}
                                />
                            </FormRow>
                            <FormColumn flex={1} gap={0}>
                                <TextNormBold>{AGENT_NAME}</TextNormBold>
                                <TextMidd className={css.agent_description}>
                                    Assign tasks or @mention for quick answers.
                                </TextMidd>
                            </FormColumn>
                            {isCurrentUserOwner && (
                                <Toggle
                                    checked={agentChecked}
                                    onChange={handleAgentToggle}
                                />
                            )}
                        </FormRow>
                    </FormColumn>
                </>
            )}

            {pendingMembers.length > 0 && (
                <>
                    <FormDivider />
                    <FormColumn gap={16}>
                        <FormTitle>Pending</FormTitle>
                        <FormColumn gap={24}>
                            {pendingMembers.map(member => (
                                <MemberRow
                                    key={member.userId}
                                    member={member}
                                    openRoleDropdownId={openRoleDropdownId}
                                    isCurrentUserOwner={isCurrentUserOwner}
                                    onOpenRoleDropdown={setOpenRoleDropdownId}
                                />
                            ))}
                        </FormColumn>
                    </FormColumn>
                </>
            )}

            {suspendedMembers.length > 0 && (

                    <>
                        <FormDivider />
                        <FormColumn gap={16}>
                            <FormTitle>Suspended</FormTitle>
                            <div className={css.suspended_members}>
                                <FormColumn gap={24}>
                                    {suspendedMembers.map(member => (
                                        <MemberRow
                                            key={member.userId}
                                            member={member}
                                            openRoleDropdownId={openRoleDropdownId}
                                            isCurrentUserOwner={isCurrentUserOwner}
                                            onOpenRoleDropdown={setOpenRoleDropdownId}
                                        />
                                    ))}
                                </FormColumn>
                            </div>
                        </FormColumn>
                    </>

            )}
        </>
    )
}

export default WorkspaceSettingsModalMembers
